import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* Sent-mail history. The message itself leaves through the Room Parent's own
   Gmail (see src/api/gmail.js), so nothing here sends anything — each row is
   the record of a send that already happened, written right after Gmail
   accepts it. The list endpoint omits body/fieldValues; the detail one has
   them. */

function normalizeSentEmail(e) {
  const to = e?.to || [];
  const cc = e?.cc || [];
  const bcc = e?.bcc || [];
  return {
    id: e?._id || e?.id || '',
    subject: e?.subject || '',
    /* only present on the detail endpoint */
    body: e?.body || '',
    to,
    cc,
    bcc,
    /* everyone the message actually reached */
    count: to.length + cc.length + bcc.length,
    sentDate: e?.sentAt || e?.createdAt || '',
    status: e?.status || 'sent',
    templateId: e?.templateId || '',
    templateName: e?.templateName || '',
    sentVia: e?.sentVia || '',
    fieldValues: e?.fieldValues || {},
    /* the files that went with it, each with where its copy is stored */
    attachments: (e?.attachments || []).map((a) => ({
      name: a?.name || '',
      size: Number(a?.size) || 0,
      kind: a?.kind || '',
      label: a?.label || '',
      url: a?.url || '',
      key: a?.key || '',
      contentType: a?.contentType || '',
    })),
  };
}

const sentEmailsService = {
  list: async (networkId, { page = 1, limit = 10, search = '', status, templateId } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.SENT_EMAILS_BY_NETWORK(networkId), {
      params: { page, limit, search, ...(status ? { status } : {}), ...(templateId ? { templateId } : {}) },
    });
    const data = res?.data ?? res;
    const rawList = data?.sentEmails || data?.docs || data?.items || (Array.isArray(data) ? data : []);
    const total = Number(data?.totalCount ?? data?.total ?? rawList.length);
    const totalPages = Number(data?.totalPages ?? Math.max(1, Math.ceil(total / limit)));
    return { emails: rawList.map(normalizeSentEmail), total, totalPages };
  },

  get: async (id) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.SENT_EMAIL_BY_ID(id));
    const data = res?.data ?? res;
    return normalizeSentEmail(data?.sentEmail || data?.data || data);
  },

  /* `to` must hold at least one address. Our class emails put the sender on To
     and everyone else on Bcc, which is exactly what Gmail was handed. */
  create: async ({ networkId, subject, body, to, cc = [], bcc = [], templateId = '', templateName = '', fieldValues = {}, attachments = [], status = 'sent' }) => {
    const res = await apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.SENT_EMAIL, {
      networkId,
      subject,
      body,
      to,
      ...(cc.length ? { cc } : {}),
      ...(bcc.length ? { bcc } : {}),
      ...(templateId ? { templateId } : {}),
      ...(templateName ? { templateName } : {}),
      ...(Object.keys(fieldValues).length ? { fieldValues } : {}),
      ...(attachments.length ? { attachments } : {}),
      sentVia: 'gmail',
      status,
    });
    const data = res?.data ?? res;
    return normalizeSentEmail(data?.data || data);
  },
};

export default sentEmailsService;
